import { useMemo } from 'react';
import { cn } from '@/lib/cn';

interface Props {
  password: string;
  /** Hide the meter entirely when the field is empty. Default true. */
  hideWhenEmpty?: boolean;
}

interface Score {
  /** 0 (very weak) to 4 (strong). */
  level: 0 | 1 | 2 | 3 | 4;
  label: string;
  hint: string | null;
}

/** Lightweight password strength estimator. Not a replacement for zxcvbn but
 *  catches the common failure modes (short, all-letters, repeated patterns)
 *  without pulling in a 400 KB dictionary. */
function scorePassword(pw: string): Score {
  if (!pw) return { level: 0, label: 'Too short', hint: null };

  let score = 0;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  if (classes >= 3) score += 1;
  if (classes >= 4) score += 1;

  // Penalties
  if (/^[a-zA-Z]+$/.test(pw)) score -= 1; // letters only
  if (/^[0-9]+$/.test(pw)) score -= 1; // digits only
  if (/(.)\1{2,}/.test(pw)) score -= 1; // 3+ repeated char
  if (/^(?:password|qwerty|12345|letmein|admin|welcome)/i.test(pw)) score -= 2;

  const clamped = Math.max(0, Math.min(4, score)) as 0 | 1 | 2 | 3 | 4;

  let hint: string | null = null;
  if (pw.length < 12) hint = 'Use at least 12 characters.';
  else if (classes < 4) hint = 'Add upper/lower case, a number, and a symbol.';

  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;

  return { level: clamped, label: labels[clamped], hint };
}

const BAR_COLORS: Record<Score['level'], string> = {
  0: 'bg-red-500',
  1: 'bg-red-400',
  2: 'bg-yellow-500',
  3: 'bg-green-500',
  4: 'bg-green-600',
};

const LABEL_COLORS: Record<Score['level'], string> = {
  0: 'text-red-600',
  1: 'text-red-600',
  2: 'text-yellow-700',
  3: 'text-green-700',
  4: 'text-green-700',
};

export function PasswordStrengthMeter({ password, hideWhenEmpty = true }: Props) {
  const score = useMemo(() => scorePassword(password), [password]);

  if (hideWhenEmpty && password.length === 0) return null;

  // 5 segments; fill segments up to `level + 1`.
  const filledSegments = score.level + 1;

  return (
    <div className="mt-1.5">
      <div className="flex gap-1" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 transition-colors',
              i < filledSegments ? BAR_COLORS[score.level] : 'bg-slate-200',
            )}
          />
        ))}
      </div>
      <div className="flex items-baseline justify-between mt-1">
        <span className={cn('text-xs font-medium', LABEL_COLORS[score.level])}>
          {score.label}
        </span>
        {score.hint ? (
          <span className="text-[11px] text-slate-500">{score.hint}</span>
        ) : null}
      </div>
    </div>
  );
}
